/** Whether the cursor input bar is open on any display. Only a window that
 *  renders the bar counts it: a mirrored copy of another display's bar can be
 *  stale (a late rebroadcast) or cleared locally (mount, space or mode
 *  changes), so no window ever reads it as the truth. Each window announces
 *  its own rendered bar and tracks the others' announcements. */
import { emit, listen } from "@tauri-apps/api/event";
import { useEffect } from "react";
import { create } from "zustand";
import { isTauri } from "@/lib/is-tauri";
import { getMyWindowLabel } from "@/lib/owner-state-client";
import { safeTauriUnlisten } from "./tauri-listener-cleanup";

const PRESENCE_EVENT = "denker://cursor-input-presence";
const QUERY_EVENT = "denker://cursor-input-query";
/** How long a window waits for the other windows to answer its query. */
const PRESENCE_SETTLE_MS = 250;

type PresencePayload = { windowLabel: string; shown: boolean; at: number };
type PresenceState = {
  /** Bars this window renders right now. */
  shownHere: number;
  /** Labels of other windows that render the bar right now. */
  otherHolders: string[];
  /** False until the other windows had time to answer this window's query,
   *  so their answers are what was already open, not new opens. */
  settled: boolean;
};

export const useCursorInputPresence = create<PresenceState>(() => ({
  shownHere: 0,
  otherHolders: [],
  settled: !isTauri(),
}));

export const cursorInputOpenSomewhere = ({
  shownHere,
  otherHolders,
}: PresenceState) => shownHere > 0 || otherHolders.length > 0;

/** Each bar renderer reports while it shows the bar, so every close path —
 *  dismissal, a local reset, a send or an unmount — releases it. The release
 *  lands a microtask later, so an effect re-run (StrictMode's mount check, a
 *  remount) never reads as the bar closing and opening again. */
export function useCursorInputShown(shown: boolean) {
  useEffect(() => {
    if (!shown) return;
    const count = (by: number) =>
      useCursorInputPresence.setState(({ shownHere }) => ({
        shownHere: shownHere + by,
      }));
    count(1);
    return () => queueMicrotask(() => count(-1));
  }, [shown]);
}

const setHolder = (label: string, holds: boolean) =>
  useCursorInputPresence.setState((state) => {
    if (state.otherHolders.includes(label) === holds) return state;
    return {
      otherHolders: holds
        ? [...state.otherHolders, label]
        : state.otherHolders.filter((item) => item !== label),
    };
  });

/** Epoch milliseconds from a monotonic clock, strictly increasing per window
 *  and above any earlier page load's stamps, so a late or reordered
 *  announcement never overrides a newer one. Rust stamps the release of a
 *  destroyed window on the same scale (`cursor_input_presence.rs`). */
let lastStamp = 0;
const announce = (shown: boolean) => {
  lastStamp = Math.max(
    performance.timeOrigin + performance.now(),
    lastStamp + 1,
  );
  const payload: PresencePayload = {
    windowLabel: getMyWindowLabel(),
    shown,
    at: lastStamp,
  };
  void emit(PRESENCE_EVENT, payload).catch(() => {});
};

/** Every desktop window announces its own bar and tracks the others. A window
 *  announces its state when it loads, which replaces any claim its previous
 *  page left behind, and releases the bar when its page goes away. */
export function attachCursorInputPresence(): () => void {
  if (!isTauri()) return () => {};
  let cancelled = false;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  useCursorInputPresence.setState({ settled: false });
  const unlisteners: Array<() => void> = [];
  const track = <T>(event: string, handler: (payload: T) => void) =>
    listen<T>(event, ({ payload }) => handler(payload)).then((unlisten) => {
      if (cancelled) safeTauriUnlisten(unlisten);
      else unlisteners.push(unlisten);
    });
  const fromOther = (label: string) => label !== getMyWindowLabel();
  const showsHere = () => useCursorInputPresence.getState().shownHere > 0;
  let shown = showsHere();
  const stopLocal = useCursorInputPresence.subscribe(() => {
    const next = showsHere();
    if (next === shown) return;
    shown = next;
    announce(next);
  });
  const release = () => announce(false);
  window.addEventListener("pagehide", release);
  const latestStamp = new Map<string, number>();
  void Promise.all([
    track<PresencePayload>(PRESENCE_EVENT, ({ windowLabel, shown: holds, at }) => {
      if (!fromOther(windowLabel) || at <= (latestStamp.get(windowLabel) ?? 0))
        return;
      latestStamp.set(windowLabel, at);
      setHolder(windowLabel, holds);
    }),
    track<{ windowLabel: string }>(QUERY_EVENT, ({ windowLabel }) => {
      if (fromOther(windowLabel) && shown) announce(true);
    }),
  ]).then(
    () => {
      if (cancelled) return;
      announce(shown);
      void emit(QUERY_EVENT, { windowLabel: getMyWindowLabel() }).catch(
        () => {},
      );
      settleTimer = setTimeout(
        () => useCursorInputPresence.setState({ settled: true }),
        PRESENCE_SETTLE_MS,
      );
    },
    () => useCursorInputPresence.setState({ settled: true }),
  );
  return () => {
    cancelled = true;
    // Untracked from here on, so it no longer claims a bar anywhere.
    if (shown) announce(false);
    stopLocal();
    window.removeEventListener("pagehide", release);
    clearTimeout(settleTimer);
    useCursorInputPresence.setState({ settled: false, otherHolders: [] });
    unlisteners.forEach(safeTauriUnlisten);
  };
}
